/**
 * Free Gift Selector for Shopify Dawn Theme
 *
 * Features:
 * - Adds free gift to cart when cart value meets threshold
 * - Enforces one free gift per order
 * - Auto-removes free gift when cart value drops below threshold
 * - Works with AJAX cart, cart drawer, and page cart
 * - Compatible with PitchPrint and other apps
 * - Does not conflict with Shopify discounts
 */

class FreeGiftSelector extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.config = {
      enabled: this.dataset.enabled === 'true',
      minimumValue: parseInt(this.dataset.minimumValue) || 5000, // in cents
      cartSubtotal: parseInt(this.dataset.cartSubtotal) || 0,
      hasFreeGift: this.dataset.hasFreeGift === 'true',
      freeGiftVariantId: this.dataset.freeGiftVariantId || null,
      collectionHandle: this.dataset.collectionHandle
    };

    // Messages (can be customized via settings)
    this.messages = {
      thresholdError: window.freeGiftStrings?.thresholdError || 'Your cart must reach $50 to select a free gift.',
      oneGiftError: window.freeGiftStrings?.oneGiftError || 'You can select only one free gift per order.',
      giftRemoved: window.freeGiftStrings?.giftRemoved || 'Your free gift has been removed because your cart value dropped below $50.',
      giftAdded: window.freeGiftStrings?.giftAdded || 'Free gift added to your cart!',
      genericError: window.freeGiftStrings?.genericError || 'Something went wrong. Please try again.'
    };

    // State
    this.isLoading = false;
    this.cartUpdateUnsubscriber = null;

    // Bind methods
    this.handleAddGift = this.handleAddGift.bind(this);
    this.handleRemoveGift = this.handleRemoveGift.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    // Add event listeners always (even if not currently eligible)
    this.addEventListeners();

    if (!this.config.enabled) return;

    // Try to subscribe to cart updates if available
    try {
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
    } catch (error) {
      console.warn('Free Gift: Cart subscribe not available, using fallback polling', error);
      // Fallback: poll for cart changes
      this.startCartPolling();
    }

    // Initial validation
    this.validateFreeGiftInCart();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    this.removeEventListeners();
    this.stopCartPolling();
  }

  /**
   * Fallback polling for cart changes
   */
  startCartPolling() {
    this.pollInterval = setInterval(() => {
      this.checkCartUpdates();
    }, 2000); // Check every 2 seconds
  }

  stopCartPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * Check for cart updates (fallback method)
   */
  async checkCartUpdates() {
    try {
      const cart = await this.getCart();
      const newSubtotal = this.calculateSubtotalExcludingGifts(cart);
      const freeGiftItem = cart.items.find(item => item.properties && item.properties._free_gift);
      
      // Check if anything changed
      if (newSubtotal !== this.config.cartSubtotal || 
          !!freeGiftItem !== this.config.hasFreeGift) {
        console.log(`Free Gift: Cart updated. New subtotal: ${newSubtotal}, Minimum: ${this.config.minimumValue}, Has gift: ${!!freeGiftItem}`);
        this.config.cartSubtotal = newSubtotal;
        this.config.hasFreeGift = !!freeGiftItem;
        if (freeGiftItem) {
          this.config.freeGiftVariantId = freeGiftItem.variant_id;
        }
        await this.validateFreeGiftInCart();
        await this.refreshSection();
      }
    } catch (error) {
      console.error('Free Gift: Error checking cart updates', error);
    }
  }

  addEventListeners() {
    // Add gift buttons
    this.querySelectorAll('[data-free-gift-add]').forEach(btn => {
      btn.addEventListener('click', this.handleAddGift);
    });

    // Remove gift button
    const removeBtn = this.querySelector('[data-free-gift-remove]');
    if (removeBtn) {
      removeBtn.addEventListener('click', this.handleRemoveGift);
    }

    // Variant selectors
    this.querySelectorAll('[data-variant-select]').forEach(select => {
      select.addEventListener('change', this.handleVariantChange);
    });
  }

  removeEventListeners() {
    this.querySelectorAll('[data-free-gift-add]').forEach(btn => {
      btn.removeEventListener('click', this.handleAddGift);
    });

    const removeBtn = this.querySelector('[data-free-gift-remove]');
    if (removeBtn) {
      removeBtn.removeEventListener('click', this.handleRemoveGift);
    }

    this.querySelectorAll('[data-variant-select]').forEach(select => {
      select.removeEventListener('change', this.handleVariantChange);
    });
  }

  /**
   * Handle cart updates from other sources
   */
  async onCartUpdate(event) {
    if (event.source === 'free-gift') return;

    // Fetch fresh cart data
    try {
      const cart = await this.getCart();
      this.config.cartSubtotal = this.calculateSubtotalExcludingGifts(cart);

      // Check for existing free gift
      const freeGiftItem = cart.items.find(item => item.properties && item.properties._free_gift);
      if (freeGiftItem) {
        this.config.hasFreeGift = true;
        this.config.freeGiftVariantId = freeGiftItem.variant_id;
      } else {
        this.config.hasFreeGift = false;
        this.config.freeGiftVariantId = null;
      }

      console.log(`Free Gift: Cart updated. New subtotal: ${this.config.cartSubtotal}, Minimum: ${this.config.minimumValue}`);

      // Validate free gift status
      await this.validateFreeGiftInCart();

      // Refresh the section
      await this.refreshSection();
    } catch (error) {
      console.error('Free Gift: Error handling cart update', error);
    }
  }

  /**
   * Calculate cart subtotal excluding free gifts
   */
  calculateSubtotalExcludingGifts(cart) {
    return cart.items.reduce((total, item) => {
      if (item.properties && item.properties._free_gift) {
        return total;
      }
      return total + item.final_line_price;
    }, 0);
  }

  /**
   * Get current cart data
   */
  async getCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return response.json();
  }

  /**
   * Validate if free gift should remain in cart
   */
  async validateFreeGiftInCart() {
    if (!this.config.hasFreeGift) return;

    if (this.config.cartSubtotal < this.config.minimumValue) {
      // Cart value dropped below threshold - remove free gift
      await this.removeFreeGiftFromCart(true);
    }
  }

  /**
   * Handle add gift button click
   */
  async handleAddGift(event) {
    event.preventDefault();

    if (this.isLoading) return;

    const button = event.currentTarget;
    const variantId = button.dataset.variantId;

    // Validate
    if (!this.canAddFreeGift()) {
      return;
    }

    // Show loading state
    this.setLoadingState(button, true);

    try {
      await this.addFreeGiftToCart(variantId);
      this.showMessage(this.messages.giftAdded, 'success');

      // Update cart display (drawer, page, etc.)
      await this.updateCartDisplay();

      // Then refresh the free gift selector section
      await this.refreshSection();
    } catch (error) {
      console.error('Free Gift: Error adding gift', error);
      this.showMessage(error.message || this.messages.genericError, 'error');
    } finally {
      this.setLoadingState(button, false);
    }
  }

  /**
   * Handle remove gift button click
   */
  async handleRemoveGift(event) {
    event.preventDefault();

    if (this.isLoading || !this.config.freeGiftVariantId) return;

    const button = event.currentTarget;
    this.setLoadingState(button, true);

    try {
      await this.removeFreeGiftFromCart(false);
      this.showMessage('Free gift removed from cart.', 'info');

      // Reload page after free gift removal
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } catch (error) {
      console.error('Free Gift: Error removing gift', error);
      this.showMessage(error.message || this.messages.genericError, 'error');
    } finally {
      this.setLoadingState(button, false);
    }
  }

  /**
   * Handle variant change
   */
  handleVariantChange(event) {
    const select = event.target;
    const card = select.closest('.free-gift-card');
    const addButton = card.querySelector('[data-free-gift-add]');
    const selectedOption = select.options[select.selectedIndex];

    addButton.dataset.variantId = select.value;

    // Update button disabled state
    if (selectedOption.disabled) {
      addButton.disabled = true;
      addButton.querySelector('.free-gift-add-btn__text').textContent = 'Sold Out';
    } else {
      addButton.disabled = false;
      addButton.querySelector('.free-gift-add-btn__text').textContent = 'Add as Free Gift';
    }
  }

  /**
   * Check if free gift can be added
   */
  canAddFreeGift() {
    // Check if cart has minimum value
    if (this.config.cartSubtotal < this.config.minimumValue) {
      this.showMessage(this.messages.thresholdError, 'error');
      return false;
    }

    // Check if already has a free gift
    if (this.config.hasFreeGift) {
      this.showMessage(this.messages.oneGiftError, 'error');
      return false;
    }

    return true;
  }

  /**
   * Add free gift to cart via AJAX API
   */
  async addFreeGiftToCart(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          '_free_gift': 'true',
          '_free_gift_source': 'free-gift-selector'
        }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || this.messages.genericError);
    }

    this.config.hasFreeGift = true;
    this.config.freeGiftVariantId = variantId;

    return response.json();
  }

  /**
   * Remove free gift from cart
   */
  async removeFreeGiftFromCart(autoRemoved = false) {
    // Find the free gift line item
    const cart = await this.getCart();
    const freeGiftItem = cart.items.find(item => item.properties && item.properties._free_gift);

    if (!freeGiftItem) {
      this.config.hasFreeGift = false;
      this.config.freeGiftVariantId = null;
      return;
    }

    // Remove by setting quantity to 0
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: freeGiftItem.key,
        quantity: 0
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || this.messages.genericError);
    }

    this.config.hasFreeGift = false;
    this.config.freeGiftVariantId = null;

    if (autoRemoved) {
      this.showMessage(this.messages.giftRemoved, 'warning');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }

    return response.json();
  }

  /**
   * Trigger cart drawer/page to update
   */
  async updateCartDisplay() {
    try {
      // Try to update cart drawer first
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        await cartDrawer.renderContents();
        return;
      }

      // Try to update cart items on full cart page
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.renderContents === 'function') {
        await cartItems.renderContents();
        return;
      }

      // Fallback: Try to trigger a cart update via pub/sub
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
        return;
      }

      // Final fallback: reload the page after a short delay
      console.warn('Free Gift: No cart update mechanism found, page will refresh automatically');
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.warn('Free Gift: Error updating cart display, falling back to page refresh', error);
      setTimeout(() => window.location.reload(), 500);
    }
  }

  /**
   * Refresh the section content
   */
  async refreshSection() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=free-gift-selector`);
      if (!response.ok) {
        console.error('Free Gift: Failed to fetch section update');
        return;
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('free-gift-selector');

      if (newSection) {
        // Update config from new section
        this.config.cartSubtotal = parseInt(newSection.dataset.cartSubtotal) || 0;
        this.config.hasFreeGift = newSection.dataset.hasFreeGift === 'true';
        this.config.freeGiftVariantId = newSection.dataset.freeGiftVariantId || null;
        this.config.enabled = newSection.dataset.enabled === 'true';

        // Replace content
        this.innerHTML = newSection.innerHTML;

        // Update classes and attributes to reflect new visibility state
        this.className = newSection.className;
        Array.from(newSection.attributes).forEach(attr => {
          if (attr.name !== 'class') {
            this.setAttribute(attr.name, attr.value);
          }
        });

        // Re-attach event listeners
        this.removeEventListeners();
        this.addEventListeners();
      }
    } catch (error) {
      console.error('Free Gift: Error refreshing section', error);
    }
  }

  /**
   * Set loading state on button
   */
  setLoadingState(button, isLoading) {
    this.isLoading = isLoading;

    if (isLoading) {
      button.disabled = true;
      const loadingEl = button.querySelector('.free-gift-add-btn__loading');
      const textEl = button.querySelector('.free-gift-add-btn__text');
      if (loadingEl) loadingEl.classList.remove('hidden');
      if (textEl) textEl.classList.add('hidden');
    } else {
      button.disabled = false;
      const loadingEl = button.querySelector('.free-gift-add-btn__loading');
      const textEl = button.querySelector('.free-gift-add-btn__text');
      if (loadingEl) loadingEl.classList.add('hidden');
      if (textEl) textEl.classList.remove('hidden');
    }
  }

  /**
   * Show message to user
   */
  showMessage(message, type = 'info') {
    const messageContainer = this.querySelector('[data-free-gift-message]');
    if (!messageContainer) return;

    const textEl = messageContainer.querySelector('.free-gift-message-text');
    const contentEl = messageContainer.querySelector('.free-gift-message-content');

    if (textEl) textEl.textContent = message;
    if (contentEl) {
      contentEl.className = 'free-gift-message-content';
      contentEl.classList.add(`free-gift-message--${type}`);
    }

    messageContainer.hidden = false;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      messageContainer.hidden = true;
    }, 5000);
  }
}

customElements.define('free-gift-selector', FreeGiftSelector);

/**
 * Global Free Gift Utilities
 * For integration with other cart features
 */
window.FreeGiftUtils = {
  /**
   * Check if cart has a free gift
   */
  async hasFreeGift() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      return cart.items.some(item => item.properties && item.properties._free_gift);
    } catch {
      return false;
    }
  },

  /**
   * Get free gift item from cart
   */
  async getFreeGiftItem() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      return cart.items.find(item => item.properties && item.properties._free_gift) || null;
    } catch {
      return null;
    }
  },

  /**
   * Validate free gift status (call after cart changes)
   */
  async validateFreeGift() {
    const selector = document.querySelector('free-gift-selector');
    if (selector && selector.validateFreeGiftInCart) {
      await selector.validateFreeGiftInCart();
    }
  },

  /**
   * Check if adding a free gift is allowed
   */
  async canAddFreeGift(minimumValue = 5000) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const hasGift = cart.items.some(item => item.properties && item.properties._free_gift);

      if (hasGift) return false;

      const subtotal = cart.items.reduce((total, item) => {
        if (item.properties && item.properties._free_gift) return total;
        return total + item.final_line_price;
      }, 0);

      return subtotal >= minimumValue;
    } catch {
      return false;
    }
  }
};

/**
 * Integration with Cart Drawer
 * Hooks into the cart drawer update process
 */
document.addEventListener('DOMContentLoaded', () => {
  // Hook into cart drawer if it exists
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const originalRenderContents = cartDrawer.renderContents;
    cartDrawer.renderContents = function(parsedState) {
      if (originalRenderContents) {
        originalRenderContents.call(this, parsedState);
      }
      // Trigger free gift validation
      window.FreeGiftUtils.validateFreeGift();
    };
  }

  // Hook into cart items for page cart
  const cartItems = document.querySelector('cart-items');
  if (cartItems) {
    const originalOnCartUpdate = cartItems.onCartUpdate;
    cartItems.onCartUpdate = function() {
      if (originalOnCartUpdate) {
        originalOnCartUpdate.call(this);
      }
      // Trigger free gift validation
      window.FreeGiftUtils.validateFreeGift();
    };
  }
});

/**
 * PitchPrint Integration
 * Ensure free gift logic works with PitchPrint customization
 */
if (window.PitchPrint) {
  // Hook into PitchPrint's cart add process if needed
  const originalPitchPrintAdd = window.PitchPrint.addToCart;
  if (originalPitchPrintAdd) {
    window.PitchPrint.addToCart = function(...args) {
      // Call original function
      const result = originalPitchPrintAdd.apply(this, args);
      // Validate free gift after PitchPrint adds to cart
      setTimeout(() => window.FreeGiftUtils.validateFreeGift(), 500);
      return result;
    };
  }
}

/**
 * Quick Add Integration
 * Prevent free gifts from being added via quick add
 */
document.addEventListener('click', async (event) => {
  const quickAddBtn = event.target.closest('[data-quick-add]');
  if (!quickAddBtn) return;

  // Check if this is a free gift product
  const variantId = quickAddBtn.dataset.variantId || quickAddBtn.dataset.productId;
  if (!variantId) return;

  // Check if product is in free gifts collection
  const freeGiftSelector = document.querySelector('free-gift-selector');
  if (!freeGiftSelector) return;

  // Let the free gift selector handle it
  const isFreeGiftProduct = freeGiftSelector.querySelector(`[data-variant-id="${variantId}"]`);
  if (isFreeGiftProduct) {
    event.preventDefault();
    event.stopPropagation();

    // Use the proper free gift add flow
    const addBtn = isFreeGiftProduct.querySelector('[data-free-gift-add]');
    if (addBtn) {
      addBtn.click();
    }
  }
}, true);
